import { Injectable } from '@angular/core';
import { BaseEntityService } from './base-entity.service';
import { CourseEntity } from '../../models/entities/course';
import { LabEntity } from '../../models/entities/lab-entity';
import { ICourseEntityService } from '../../interfaces/entities/course-entity-service.interface';

@Injectable({
  providedIn: 'root'
})
export class CourseEntityService extends BaseEntityService<CourseEntity> implements ICourseEntityService {
  constructor() {
    super();
  }
  /**
   * Initialize the service with course data
   * @protected
   */
  protected initializeData(): void {
    // Create Course 1: Azure Fundamentals
    const course1 = new CourseEntity();
    course1.id = '550e8400-e29b-41d4-a716-446655440010';
    course1.name = 'Azure Fundamentals';
    course1.code = 'AZ-900';
    course1.provider = 'Microsoft';
    course1.description = 'Learn the basics of Azure cloud services, core solutions, and management tools.';
    course1.createdBy = 'system';
    course1.createdOn = new Date('2024-01-15');
    course1.updatedBy = 'system';
    course1.updatedOn = new Date('2024-01-15');

    course1.vmImage.cloudProviderData.provider = 'azure';
    course1.vmImage.cloudProviderData.region = 'centralindia';
    course1.vmImage.cloudProviderData.size = 'Standard_B1s';
    course1.vmImage.cloudProviderData.storage = 'Standard_LRS';

    course1.vmImage.softwareData.os = 'windows';
    course1.vmImage.softwareData.osVersion = 'win10';
    course1.vmImage.softwareData.sdks = [];
    course1.vmImage.softwareData.databases = [];
    course1.vmImage.softwareData.editors = [];
    course1.vmImage.softwareData.misc = ['chrome'];

    const lab1 = new LabEntity();
    lab1.id = '550e8400-e29b-41d4-a716-446655440011';
    lab1.name = 'Introduction to Azure';
    lab1.code = 'LAB01';
    lab1.labOrder = 1;
    lab1.description = 'Getting familiar with Azure portal and basic services';
    lab1.durationInHours = 2;
    lab1.hasHandsOn = true;
    lab1.labContents = ['Azure Portal Overview: The Azure Portal is a web-based interface that lets users manage and monitor Azure resources through a unified dashboard. It offers tools for configuring services, tracking usage, and managing security, making cloud management easier and more efficient.'
      , 'Creating Resource Groups:Creating Resource Groups in Azure involves organizing related resources like virtual machines, databases, and storage into a single container. This helps manage, monitor, and apply permissions or policies efficiently across grouped services.'
      , 'Basic Virtual Machines:Basic Virtual Machines in Azure provide cost-effective compute options for development, testing, and light workloads. They offer essential features with lower pricing, making them ideal for non-critical applications and entry-level tasks.'];
    lab1.labContentsUrl = 'https://docs.microsoft.com/azure/fundamentals/lab01';
    lab1.createdBy = 'system';
    lab1.createdOn = new Date('2024-01-15');
    lab1.updatedBy = 'system';
    lab1.updatedOn = new Date('2024-01-15');

    const lab2 = new LabEntity();
    lab2.id = '550e8400-e29b-41d4-a716-446655440012';
    lab2.name = 'Azure Storage';
    lab2.code = 'LAB02';
    lab2.labOrder = 2;
    lab2.description = 'Exploring different storage options in Azure';
    lab2.durationInHours = 3;
    lab2.hasHandsOn = true;
    lab2.labContents = ['Blob Storage', 'File Storage', 'Storage Accounts'];
    lab2.labContentsUrl = 'https://docs.microsoft.com/azure/fundamentals/lab02';
    lab2.createdBy = 'system';
    lab2.createdOn = new Date('2024-01-15');
    lab2.updatedBy = 'system';
    lab2.updatedOn = new Date('2024-01-15');

    course1.labs = [lab1, lab2];

    // Create Course 2: ASP.NET Core Web API Development
    const course2 = new CourseEntity();
    course2.id = '6ba7b810-9dad-11d1-80b4-00c04fd43010';
    course2.name = 'ASP.NET Core Web API Development';
    course2.code = 'NET-API-8';
    course2.provider = 'Microsoft';
    course2.description = 'Master building RESTful Web APIs with ASP.NET Core, Entity Framework Core, and SQL Server. Learn authentication, authorization, and best practices.';
    course2.createdBy = 'system';
    course2.createdOn = new Date('2024-02-01');
    course2.updatedBy = 'system';
    course2.updatedOn = new Date('2024-02-01');

    course2.vmImage.cloudProviderData.provider = 'azure';
    course2.vmImage.cloudProviderData.region = 'eastus';
    course2.vmImage.cloudProviderData.size = 'Standard_D2s_v3';
    course2.vmImage.cloudProviderData.storage = 'Standard_LRS';

    course2.vmImage.softwareData.os = 'windows';
    course2.vmImage.softwareData.osVersion = 'win11';
    course2.vmImage.softwareData.sdks = ['dotnet8'];
    course2.vmImage.softwareData.databases = ['sqlserver2022'];
    course2.vmImage.softwareData.editors = ['vscode', 'vs2022'];
    course2.vmImage.softwareData.misc = ['postman', 'git'];

    const lab3 = new LabEntity();
    lab3.id = '6ba7b810-9dad-11d1-80b4-00c04fd43011';
    lab3.name = 'Creating Your First Web API';
    lab3.code = 'LAB03';
    lab3.labOrder = 1;
    lab3.description = 'Build a basic RESTful API with controllers and HTTP verbs';
    lab3.durationInHours = 4;
    lab3.hasHandsOn = true;
    lab3.labContents = ['Setting up ASP.NET Core Project', 'Creating Controllers', 'Implementing GET/POST/PUT/DELETE'];
    lab3.labContentsUrl = 'https://learn.microsoft.com/aspnet-core/web-api/lab03';
    lab3.createdBy = 'system';
    lab3.createdOn = new Date('2024-02-01');
    lab3.updatedBy = 'system';
    lab3.updatedOn = new Date('2024-02-01');

    const lab4 = new LabEntity();
    lab4.id = '6ba7b810-9dad-11d1-80b4-00c04fd43012';
    lab4.name = 'Entity Framework Core Integration';
    lab4.code = 'LAB04';
    lab4.labOrder = 2;
    lab4.description = 'Connect your API to SQL Server using Entity Framework Core';
    lab4.durationInHours = 5;
    lab4.hasHandsOn = true;
    lab4.labContents = ['DbContext Setup', 'Entity Models', 'Migrations', 'CRUD Operations'];
    lab4.labContentsUrl = 'https://learn.microsoft.com/aspnet-core/web-api/lab04';
    lab4.createdBy = 'system';
    lab4.createdOn = new Date('2024-02-01');
    lab4.updatedBy = 'system';
    lab4.updatedOn = new Date('2024-02-01');

    const lab5 = new LabEntity();
    lab5.id = '6ba7b810-9dad-11d1-80b4-00c04fd43013';
    lab5.name = 'Authentication & Authorization';
    lab5.code = 'LAB05';
    lab5.labOrder = 3;
    lab5.description = 'Implement JWT authentication and role-based authorization';
    lab5.durationInHours = 6;
    lab5.hasHandsOn = true;
    lab5.labContents = ['JWT Setup', 'User Authentication', 'Role-based Authorization', 'Secure Endpoints'];
    lab5.labContentsUrl = 'https://learn.microsoft.com/aspnet-core/web-api/lab05';
    lab5.createdBy = 'system';
    lab5.createdOn = new Date('2024-02-01');
    lab5.updatedBy = 'system';
    lab5.updatedOn = new Date('2024-02-01');

    course2.labs = [lab3, lab4, lab5];

    // Create Course 3: GCP Professional Cloud Architect
    const course3 = new CourseEntity();
    course3.id = '6ba7b811-9dad-11d1-80b4-00c04fd43012';
    course3.name = 'GCP Professional Cloud Architect';
    course3.code = 'GCP-PCA';
    course3.provider = 'Google';
    course3.description = 'Design, develop, and manage robust, secure, scalable solutions on Google Cloud Platform.';
    course3.createdBy = 'system';
    course3.createdOn = new Date('2024-03-10');
    course3.updatedBy = 'system';
    course3.updatedOn = new Date('2024-03-10');

    course3.vmImage.cloudProviderData.provider = 'gcp';
    course3.vmImage.cloudProviderData.region = 'asia-east1';
    course3.vmImage.cloudProviderData.size = 'e2-micro';
    course3.vmImage.cloudProviderData.storage = 'STANDARD';

    course3.vmImage.softwareData.os = 'linux';
    course3.vmImage.softwareData.osVersion = 'ubuntu-24.04';
    course3.vmImage.softwareData.sdks = [];
    course3.vmImage.softwareData.databases = [];
    course3.vmImage.softwareData.editors = ['vscode'];
    course3.vmImage.softwareData.misc = ['git', 'chrome'];

    const lab6 = new LabEntity();
    lab6.id = '6ba7b811-9dad-11d1-80b4-00c04fd43013';
    lab6.name = 'Compute Engine and Load Balancing';
    lab6.code = 'LAB06';
    lab6.labOrder = 1;
    lab6.description = 'Creating and managing Compute Engine instances with load balancers';
    lab6.durationInHours = 3.5;
    lab6.hasHandsOn = true;
    lab6.labContents = ['Compute Engine Setup', 'Instance Groups', 'Load Balancer Configuration'];
    lab6.labContentsUrl = 'https://cloud.google.com/training/pca/lab06';
    lab6.createdBy = 'system';
    lab6.createdOn = new Date('2024-03-10');
    lab6.updatedBy = 'system';
    lab6.updatedOn = new Date('2024-03-10');

    course3.labs = [lab6];

    // Create Course 4: custom course for testing
    const course4 = new CourseEntity();
    course4.id = '-1';
    course4.name = 'Custom Course';
    course4.code = 'Custom';
    course4.provider = 'Custom';
    course4.description = 'Design, develop, and manage robust, secure, scalable solutions on Google Cloud Platform.';
    course4.createdBy = 'system';
    course4.createdOn = new Date('2024-03-10');
    course4.updatedBy = 'system';
    course4.updatedOn = new Date('2024-03-10');

    course4.vmImage.cloudProviderData.provider = 'gcp';
    course4.vmImage.cloudProviderData.region = 'asia-east1';
    course4.vmImage.cloudProviderData.size = 'e2-micro';
    course4.vmImage.cloudProviderData.storage = 'STANDARD';

    course4.vmImage.softwareData.os = 'linux';
    course4.vmImage.softwareData.osVersion = 'ubuntu-24.04';
    course4.vmImage.softwareData.sdks = [];
    course4.vmImage.softwareData.databases = [];
    course4.vmImage.softwareData.editors = ['vscode'];
    course4.vmImage.softwareData.misc = ['git', 'chrome'];

    
    course3.labs = [];



    this.entities =[];// [course1, course2, course3, course4];
  }

  /** @inheritDoc */
  createEntity(course: CourseEntity, createdBy: string): CourseEntity {
    const newCourse = new CourseEntity();
    this.setCreateAuditProperties(newCourse, createdBy);

    newCourse.name = course.name;
    newCourse.code = course.code;
    newCourse.provider = course.provider;
    newCourse.description = course.description;

    // Deep copy VM Image
    newCourse.vmImage.cloudProviderData.provider = course.vmImage.cloudProviderData.provider;
    newCourse.vmImage.cloudProviderData.region = course.vmImage.cloudProviderData.region;
    newCourse.vmImage.cloudProviderData.size = course.vmImage.cloudProviderData.size;
    newCourse.vmImage.cloudProviderData.storage = course.vmImage.cloudProviderData.storage;

    newCourse.vmImage.softwareData.os = course.vmImage.softwareData.os;
    newCourse.vmImage.softwareData.osVersion = course.vmImage.softwareData.osVersion;
    newCourse.vmImage.softwareData.sdks = [...course.vmImage.softwareData.sdks];
    newCourse.vmImage.softwareData.databases = [...course.vmImage.softwareData.databases];
    newCourse.vmImage.softwareData.editors = [...course.vmImage.softwareData.editors];
    newCourse.vmImage.softwareData.misc = [...course.vmImage.softwareData.misc];

    // Deep copy labs
    newCourse.labs = course.labs.map(lab => {
      const newLab = new LabEntity();
      newLab.id = this.generateGuid();
      newLab.name = lab.name;
      newLab.code = lab.code;
      newLab.labOrder = lab.labOrder;
      newLab.description = lab.description;
      newLab.durationInHours = lab.durationInHours;
      newLab.hasHandsOn = lab.hasHandsOn;
      newLab.labContents = [...lab.labContents];
      newLab.labContentsUrl = lab.labContentsUrl;
      newLab.createdBy = createdBy;
      newLab.createdOn = new Date();
      newLab.updatedBy = createdBy;
      newLab.updatedOn = new Date();
      return newLab;
    });

    this.entities.push(newCourse);
    return newCourse;
  }

  /** @inheritDoc */
  updateEntity(id: string, updates: Partial<CourseEntity>, updatedBy: string): CourseEntity | null {
    const courseIndex = this.entities.findIndex(course => course.id === id);

    if (courseIndex === -1) {
      return null;
    }

    const course = this.entities[courseIndex];
    this.setUpdateAuditProperties(course, updatedBy);

    // Update basic properties
    if (updates.name !== undefined) course.name = updates.name;
    if (updates.code !== undefined) course.code = updates.code;
    if (updates.provider !== undefined) course.provider = updates.provider;
    if (updates.description !== undefined) course.description = updates.description;

    // Update VM Image if provided
    if (updates.vmImage !== undefined) {
      if (updates.vmImage.cloudProviderData !== undefined) {
        const cloudData = updates.vmImage.cloudProviderData;
        if (cloudData.provider !== undefined) course.vmImage.cloudProviderData.provider = cloudData.provider;
        if (cloudData.region !== undefined) course.vmImage.cloudProviderData.region = cloudData.region;
        if (cloudData.size !== undefined) course.vmImage.cloudProviderData.size = cloudData.size;
        if (cloudData.storage !== undefined) course.vmImage.cloudProviderData.storage = cloudData.storage;
      }

      if (updates.vmImage.softwareData !== undefined) {
        const softwareData = updates.vmImage.softwareData;
        if (softwareData.os !== undefined) course.vmImage.softwareData.os = softwareData.os;
        if (softwareData.osVersion !== undefined) course.vmImage.softwareData.osVersion = softwareData.osVersion;
        if (softwareData.sdks !== undefined) course.vmImage.softwareData.sdks = [...softwareData.sdks];
        if (softwareData.databases !== undefined) course.vmImage.softwareData.databases = [...softwareData.databases];
        if (softwareData.editors !== undefined) course.vmImage.softwareData.editors = [...softwareData.editors];
        if (softwareData.misc !== undefined) course.vmImage.softwareData.misc = [...softwareData.misc];
      }
    }

    // Update labs if provided
    if (updates.labs !== undefined) {
      course.labs = updates.labs.map(lab => {
        const updatedLab = new LabEntity();
        updatedLab.id = lab.id || this.generateGuid();
        updatedLab.name = lab.name || '';
        updatedLab.code = lab.code || '';
        updatedLab.labOrder = lab.labOrder || 0;
        updatedLab.description = lab.description || '';
        updatedLab.durationInHours = lab.durationInHours || 0;
        updatedLab.hasHandsOn = lab.hasHandsOn || false;
        updatedLab.labContents = [...(lab.labContents || [])];
        updatedLab.labContentsUrl = lab.labContentsUrl || '';

        // Set audit properties for lab
        if (lab.id && course.labs.find(existingLab => existingLab.id === lab.id)) {
          // Existing lab - keep original creation info
          const existingLab = course.labs.find(existingLab => existingLab.id === lab.id)!;
          updatedLab.createdBy = existingLab.createdBy;
          updatedLab.createdOn = existingLab.createdOn;
        } else {
          // New lab
          updatedLab.createdBy = updatedBy;
          updatedLab.createdOn = new Date();
        }
        updatedLab.updatedBy = updatedBy;
        updatedLab.updatedOn = new Date();

        return updatedLab;
      });
    }

    return course;
  }

  /** @inheritDoc */
  getAllCourses(): CourseEntity[] {
    return this.getAllEntities();
  }

  /** @inheritDoc */
  getCourseById(id: string): CourseEntity | undefined {
    return this.getEntityById(id);
  }

  /** @inheritDoc */
  createCourse(course: CourseEntity, createdBy: string): CourseEntity {
    return this.createEntity(course, createdBy);
  }

  /** @inheritDoc */
  updateCourse(id: string, updates: Partial<CourseEntity>, updatedBy: string): CourseEntity | null {
    return this.updateEntity(id, updates, updatedBy);
  }

  /** @inheritDoc */
  deleteCourse(id: string, deletedBy: string): boolean {
    return this.deleteEntity(id, deletedBy);
  }

  /** @inheritDoc */
  getCoursesByProvider(provider: string): CourseEntity[] {
    return this.entities.filter(course =>
      course.provider.toLowerCase() === provider.toLowerCase()
    );
  }
}
